import React from 'react';
import styled from 'styled-components';
import Link from '@material-ui/core/Link';
import ReactPlayer from 'react-player';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';

import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

import { BodyParent, BodyChildren } from './animation/variants';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '375px',
      flexDirection: 'column-reverse !important',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: '950px',
      justifyContent: 'space-between',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: '1085px',
    },
  },
  laptopBooks: {
    [theme.breakpoints.down('sm')]: {
      width: '375px',
      height: '380px',
    },
    [theme.breakpoints.up('md')]: {
      width: '435px',
      height: '440px',
    },
    [theme.breakpoints.up('lg')]: {
      width: '545px',
      height: '555px',
    },
  },
  title: {
    color: theme.palette.secondary.main,
  },
  description: {
    maxWidth: '950px',
    fontSize: 24,
    fontStyle: 'normal',
    fontWeight: 'normal',
    lineHeight: '31px',
    letterSpacing: '-0.04em',
  },
  videoWrapper: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      maxWidth: '350px',
    },
    [theme.breakpoints.up('md')]: {
      maxWidth: '450px',
    },
    [theme.breakpoints.up('lg')]: {
      maxWidth: '600px',
    },
  },
  videoCanvas: {
    borderRadius: '16px',
    overflow: 'hidden',
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      width: '330px',
      height: '200px',
    },
    [theme.breakpoints.up('md')]: {
      width: '420px',
      height: '275px',
    },
    [theme.breakpoints.up('lg')]: {
      width: '520px',
      height: '290px',
    },
  },
  alienWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  alien: {
    width: '160px',
    height: '165px',
    position: 'relative',
    zIndex: 100,
    [theme.breakpoints.down('sm')]: {
      marginTop: '-550px',
      marginRight: '-20px',
    },
    [theme.breakpoints.up('md')]: {
      marginTop: '-370px',
      marginRight: '-100px',
    },
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '35px',
    },
    [theme.breakpoints.up('md')]: {
      marginTop: '10px',
      marginLeft: '80px',
      alignItems: 'flex-start',
    },
  },
  revisitTitle: {
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      fontSize: 32,
      lineHeight: '32px',
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 48,
      lineHeight: '48px',
    },
  },
  revisitStats: {
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      marginTop: '15px',
    },
    [theme.breakpoints.up('md')]: {
      marginTop: '35px',
    },
  },

  statTitle: {
    color: theme.palette.secondary.main,
  },
  statValue: {
    [theme.breakpoints.down('sm')]: {
      fontSize: 24,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 36,
    },
  },
  statCategory: {
    lineHeight: '31px',
    margin: '0',
    [theme.breakpoints.down('sm')]: {
      fontSize: 18,
    },
    [theme.breakpoints.up('md')]: {
      fontSize: 24,
    },
  },
  statItem: {
    display: 'flex',
    flexDirection: 'column',
  },
  revisitSubtext: {
    marginTop: '10px',
    fontSize: 16,
    lineHeight: '21px',
  },
  revisitLinkText: {
    color: theme.palette.secondary.light,
  },
}));

const Wrapper = styled.div`
  display: flex;
  margin-top: 20px;
`;

const VideoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Body = styled.div``;

const StatItemsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-left: 7px;
  & > * {
    display: flex;
    align-items: center;
    text-align: center;
  }
`;

const StatItem = ({ category, value }) => {
  const classes = useStyles();
  return (
    <div className={classes.statItem}>
      <div className={classes.statValue}>
        <b>{value}</b>
      </div>
      <p className={classes.statCategory}>{category}</p>
    </div>
  );
};

const Revisit = () => {
  const [ref, inView] = useInView({
    rootMargin: '-100px 100px',
    triggerOnce: true,
  });
  const classes = useStyles();

  return (
    <motion.div
      ref={ref}
      variants={BodyParent}
      initial="initial"
      animate={inView ? 'enter' : 'initial'}>
      <Wrapper className={classes.root}>
        <VideoWrapper className={classes.videoWrapper}>
          <motion.div className={classes.videoCanvas} variants={BodyChildren}>
            <ReactPlayer
              url="https://www.youtube.com/watch?v=P5_DZDgfH1c&feature=youtu.be"
              width="100%"
              height="100%"
            />
          </motion.div>
          <motion.div className={classes.alienWrapper} variants={BodyChildren}>
            <img
              src={require('./assets/alien.png')}
              alt="alien"
              className={classes.alien}
            />
          </motion.div>
        </VideoWrapper>
        <Body className={classes.body}>
          <motion.div className={classes.revisitTitle} variants={BodyChildren}>
            <b>Revisit</b>
            <b style={{ color: '#E5A55E', width: '351px' }}>Hack Western 6</b>
          </motion.div>
          <motion.div className={classes.revisitStats} variants={BodyChildren}>
            <Typography variant="h2" className={classes.statTitle}>
              Stats At A Glance
            </Typography>
            <StatItemsWrapper>
              <StatItem category="Hackers" value="480+" />
              <StatItem category="Projects" value="92" />
              <StatItem category="In Prizes" value="$13,700" />
            </StatItemsWrapper>
          </motion.div>
          <motion.div variants={BodyChildren}>
            <Typography variant="body1" className={classes.revisitSubtext}>
              Relive the space adventure&nbsp;
              <Link
                href="/2019"
                target="_Blank"
                className={classes.revisitLinkText}>
                here!
              </Link>
            </Typography>
          </motion.div>
        </Body>
      </Wrapper>
    </motion.div>
  );
};

export default Revisit;
